import {
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { CHAINS } from '../config/chains';
import { withRateLimitRetry, isRateLimitError } from '../utils/rateLimitRetry';

/**
 * Firestore collection for operation history reads (backend writes the same collection).
 * Override with REACT_APP_REQUESTS_COLLECTION only for local migration / legacy data.
 */
export const REQUESTS_COLLECTION_NAME =
  process.env.REACT_APP_REQUESTS_COLLECTION || 'requests';

const REQUESTS_COLLECTION = REQUESTS_COLLECTION_NAME;

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

/** Numeric chain id for Firestore filters; `chainIdDecimal` may be 0 (Stellar) — do not use `||`. */
export function numericChainIdFromChainKey(chainKey) {
  if (!chainKey || typeof chainKey !== 'string' || !CHAINS[chainKey]) return NaN;
  const c = CHAINS[chainKey];
  if (typeof c.chainIdDecimal === 'number' && !Number.isNaN(c.chainIdDecimal)) return c.chainIdDecimal;
  if (typeof c.chainId === 'string' && c.chainId.startsWith('0x')) return parseInt(c.chainId, 16);
  const n = Number(c.chainId);
  return Number.isFinite(n) ? n : NaN;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function getBackendApiBase() {
  const v = (BACKEND_API_URL || '').trim().replace(/\/+$/, '');
  if (!v) {
    throw new Error('Missing REACT_APP_BACKEND_API_URL');
  }
  return v;
}

async function apiFetch(path, options = {}) {
  const base = getBackendApiBase();
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const msg = payload?.error || `API request failed (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

/** Rich console diagnostics when POST /api/requests fails (status, body, payload snapshot). */
function logRequestCreateFailure(error, requestData, label = 'RequestService') {
  const status = error?.status;
  const payload = error?.payload;
  const summary = requestData
    ? {
        requestId: requestData.requestId,
        userAddress: requestData.userAddress,
        chainId: requestData.chainId,
        chainKey: requestData.chainKey,
        operationKind: requestData.operationKind,
        type: requestData.type,
        transactionHash: requestData.transactionHash,
        details: requestData.details,
        hasSenderCipher: Boolean(requestData.encryptedSenderRandomness),
        hasReceiverCipher: Boolean(requestData.encryptedReceiverRandomness),
      }
    : null;
  console.error(`[${label}] POST /api/requests failed`, {
    message: error?.message,
    status,
    serverError: payload?.error,
    serverIssues: payload?.issues,
    fullPayload: payload,
    requestSummary: summary,
    stack: error?.stack,
  });
}

const isRetryableError = (error) => {
  if (isRateLimitError(error)) {
    return true;
  }

  const code = error?.code;
  const message = error?.message?.toLowerCase() || '';

  const retryableCodes = [
    'unavailable',
    'deadline-exceeded',
    'aborted',
    'internal',
    'unauthenticated',
  ];

  if (code && retryableCodes.includes(code.toLowerCase())) {
    return true;
  }

  if (
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('fetch') ||
    message.includes('connection')
  ) {
    return true;
  }

  return false;
};

/** Map a Firestore doc to a request row; returns null if chainId filter excludes the doc. */
function buildRequestItem(doc, chainId) {
  const data = doc.data();
  // Stellar uses chainId 0 — must not use `if (!chainId)`.
  if (chainId === null || chainId === undefined) {
    return { id: doc.id, ...data };
  }

  let docChainId = data.chainId;
  let filterChainId = chainId;

  if (typeof docChainId === 'string' && CHAINS[docChainId]) {
    const chainConfig = CHAINS[docChainId];
    docChainId =
      chainConfig.chainIdDecimal ??
      (typeof chainConfig.chainId === 'string' && chainConfig.chainId.startsWith('0x')
        ? parseInt(chainConfig.chainId, 16)
        : Number(chainConfig.chainId));
  } else if (typeof docChainId === 'string' && docChainId.startsWith('0x')) {
    docChainId = parseInt(docChainId, 16);
  } else {
    docChainId = Number(docChainId);
  }

  if (typeof filterChainId === 'string' && CHAINS[filterChainId]) {
    const chainConfig = CHAINS[filterChainId];
    filterChainId =
      chainConfig.chainIdDecimal ??
      (typeof chainConfig.chainId === 'string' && chainConfig.chainId.startsWith('0x')
        ? parseInt(chainConfig.chainId, 16)
        : Number(chainConfig.chainId));
  } else if (typeof filterChainId === 'string' && filterChainId.startsWith('0x')) {
    filterChainId = parseInt(filterChainId, 16);
  } else {
    filterChainId = Number(filterChainId);
  }

  let matchesById = false;
  if (doc.id.includes(':')) {
    const docIdParts = doc.id.split(':');
    const docIdChainKey = docIdParts[0];
    if (CHAINS[docIdChainKey]) {
      const chainConfig = CHAINS[docIdChainKey];
      const docIdChainId =
        chainConfig.chainIdDecimal ??
        (typeof chainConfig.chainId === 'string' && chainConfig.chainId.startsWith('0x')
          ? parseInt(chainConfig.chainId, 16)
          : Number(chainConfig.chainId));
      matchesById = docIdChainId === filterChainId;
    }
  }

  const chainIdsMatch =
    !Number.isNaN(docChainId) && !Number.isNaN(filterChainId) && docChainId === filterChainId;
  if (chainIdsMatch || matchesById) {
    return { id: doc.id, ...data };
  }
  return null;
}

function requestCreatedAtMs(row) {
  const ca = row?.createdAt;
  if (ca && typeof ca.toMillis === 'function') return ca.toMillis();
  if (ca instanceof Date) return ca.getTime();
  if (typeof ca?.seconds === 'number') return ca.seconds * 1000 + (ca.nanoseconds || 0) / 1e6;
  return 0;
}

function filterSortAndLimitRequestSnapshot(snapshot, chainId, maxRequests) {
  const rows = [];
  snapshot.forEach((d) => {
    const row = buildRequestItem(d, chainId);
    if (row) rows.push(row);
  });
  rows.sort((a, b) => requestCreatedAtMs(b) - requestCreatedAtMs(a));
  if (rows.length > maxRequests) {
    console.warn(
      `Warning: ${rows.length} matching requests; showing newest ${maxRequests} only.`,
    );
    return rows.slice(0, maxRequests);
  }
  return rows;
}

export class RequestService {
  /**
   * Create request via backend API (writes to Firestore server-side); same pattern as EVM frontend-react.
   */
  static async addRequest(requestData, retryOptions = { maxRetries: 3, baseDelayMs: 1000 }) {
    const { maxRetries = 3, baseDelayMs = 1000 } = retryOptions;

    const { requestId } = requestData;
    let lastError = null;

    return await withRateLimitRetry(
      async () => {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            if (attempt > 0) {
              const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
              console.log(
                `Retrying request write (attempt ${attempt + 1}/${maxRetries + 1}) after ${delayMs}ms...`,
              );
              await sleep(delayMs);
            }

            console.log(`Adding request via backend API (attempt ${attempt + 1}):`, requestData);
            const result = await apiFetch('/api/requests', {
              method: 'POST',
              body: JSON.stringify(requestData),
            });
            const id = result?.id || requestId;
            console.log('Request successfully added with ID:', id);
            return id;
          } catch (error) {
            lastError = error;
            console.error(`Error adding request (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
            logRequestCreateFailure(error, requestData, 'RequestService.addRequest');

            if (isRateLimitError(error)) {
              throw error;
            }

            if (attempt < maxRetries && isRetryableError(error)) {
              console.log('Retryable error detected, will retry...');
              continue;
            }

            if (!isRetryableError(error) || attempt === maxRetries) {
              throw error;
            }
          }
        }

        throw lastError || new Error('Failed to add request after all retries');
      },
      {
        retries: 5,
        baseDelayMs: 1000,
        maxDelayMs: 30000,
      },
    );
  }

  /**
   * Backend stores immutable request rows; client updates are no-ops (parity with frontend-react).
   */
  static async updateRequestStatus(requestId, status, additionalData = {}, chainId = null, userAddress = null) {
    return await withRateLimitRetry(
      async () => {
        try {
          let documentId = requestId;

          if (!requestId.includes(':')) {
            if (chainId !== null && chainId !== undefined && userAddress) {
              documentId = `${String(chainId)}:${String(userAddress).toLowerCase()}:${requestId}`;
            } else if (chainId !== null && chainId !== undefined) {
              documentId = `${String(chainId)}:${requestId}`;
            }
          }

          console.log('Skipping immutable request status update:', documentId, status, additionalData);
        } catch (error) {
          console.error('Error updating request status:', error);
          throw error;
        }
      },
      {
        retries: 5,
        baseDelayMs: 1000,
        maxDelayMs: 30000,
      },
    );
  }

  /** Read history from Firestore (same collection the API writes). */
  static async getUserRequests(userAddress, chainId = null) {
    return await withRateLimitRetry(
      async () => {
        try {
          const MAX_REQUESTS = 10000;
          const addr = String(userAddress || '').toLowerCase();
          if (!addr) return [];

          const qByUserAddress = query(
            collection(db, REQUESTS_COLLECTION),
            where('schemaVersion', '==', 2),
            where('userAddress', '==', addr),
          );

          const snap = await getDocs(qByUserAddress);
          return filterSortAndLimitRequestSnapshot(snap, chainId, MAX_REQUESTS);
        } catch (error) {
          console.error('Error getting user requests:', error);
          throw error;
        }
      },
      {
        retries: 5,
        baseDelayMs: 1000,
        maxDelayMs: 30000,
      },
    );
  }

  static subscribeToUserRequests(userAddress, callback, chainId = null) {
    if (!userAddress) return () => {};

    const MAX_REQUESTS = 10000;
    const addr = String(userAddress || '').toLowerCase();
    if (!addr) return () => {};

    const qByUserAddress = query(
      collection(db, REQUESTS_COLLECTION),
      where('schemaVersion', '==', 2),
      where('userAddress', '==', addr),
    );

    const unsubOwned = onSnapshot(
      qByUserAddress,
      (s) => {
        const requests = filterSortAndLimitRequestSnapshot(s, chainId, MAX_REQUESTS);
        callback(requests);
      },
      (error) => {
        console.error('Error listening to user requests:', error);
        try {
          callback([]);
        } catch (cbErr) {
          console.error('Request history callback failed after snapshot error:', cbErr);
        }
      },
    );
    return () => unsubOwned();
  }

  static async deleteRequest(requestId) {
    console.error('deleteRequest called but deletion is disabled. Request ID:', requestId);
    throw new Error('Request deletion is not allowed');
  }

  static async cleanupOldRequests(userAddress, olderThanDays = 30) {
    console.error('cleanupOldRequests called but deletion is disabled. User:', userAddress);
    throw new Error('Request deletion is not allowed');
  }
}
